import React, { memo, useContext } from 'react';
import PropTypes from 'prop-types';
import { AppContext } from 'src/components/app-context';
import { ProductCard } from 'src/components/product-card';
import ProductRow from 'src/components/product-row';
import { ListItem } from 'src/components/list-item';
import { ProductImage } from 'src/components/product-image';

import {
  StyledListItem,
  StoreLogo,
  OutlinkCallToAction,
  OutlinkIcon,
} from './styles';

const SearchItem = ({
  item,
  onClick,
  onRawClick,
  type,
  mode,
  query,
  onSelect,
  styling,
  callToActionText,
  showStoreLogo,
}) => {
  const { t, linkTo } = useContext(AppContext);

  const qs = {
    q: query,
  };
  const externalLinkAttributes = {
    target: '_blank',
    rel: 'nofollow',
  };

  const onClickHandler = e => {
    if (onSelect) {
      e.preventDefault();
      onSelect(item);
    }
    onClick(type, item, e);

    if (onRawClick && type === 'raw') {
      onRawClick(item);
    }
  };

  const link = linkTo(type, item, qs);

  switch (type) {
    case 'product':
    case 'raw_sorted': {
      if (styling === 'simple') {
        return (
          <ListItem name={item.name} link={link} onClick={onClickHandler} />
        );
      }

      return mode === 'row' ? (
        <ProductRow
          onClick={onClickHandler}
          name={item.name}
          link={link}
          price={item.priceRaw ? item.priceRaw : null}
          subtitle={item.category && item.category.name}
          image={<ProductImage name={item.name} src={item.image} />}
          rating={item.rating}
          ratingCount={item.ratingCount}
          storesCount={item.priceCount}
          storesCountText={t('campaigns', 'item.sold-by-n-stores', {
            n: item.priceCount,
          })}
          showDivider
        />
      ) : (
        <ProductCard
          id={item.id}
          onClick={onClickHandler}
          name={item.name}
          link={link}
          price={item.price ? item.price : null}
          subtitle={item.category && item.category.name}
          image={<ProductImage name={item.name} src={item.image} />}
          rating={item.rating}
          ratingCount={item.ratingCount}
        />
      );
    }
    case 'raw':
      if (styling === 'simple') {
        return (
          <ListItem
            name={item.name}
            link={link}
            onClick={onClickHandler}
            linkAttributes={externalLinkAttributes}
          />
        );
      }

      return mode === 'row' ? (
        <ProductRow
          onClick={onClickHandler}
          name={item.name}
          price={item.price ? item.price : null}
          link={link}
          image={<ProductImage name={item.name} />}
          subtitle={<Outlink store={item.store} />}
          linkAttributes={externalLinkAttributes}
          showDivider
        />
      ) : (
        <ProductCard
          onClick={onClickHandler}
          name={item.name}
          price={item.price ? item.price : null}
          link={link}
          image={<ProductImage name={item.name} />}
          subtitle={<Outlink store={item.store} />}
          linkAttributes={externalLinkAttributes}
        />
      );

    case 'brand': {
      return (
        <ListItem
          name={item.name}
          link={link}
          onClick={onClickHandler}
          showArrow
        />
      );
    }
    case 'store': {
      return (
        <ListItem
          name={item.name}
          link={link}
          onClick={onClickHandler}
          image={
            showStoreLogo &&
            item.logo && (
              <StoreLogo storeId={item.id} name={item.name} hasImage featured />
            )
          }
          showArrow
        />
      );
    }
    case 'brand_category':
    case 'brand_virtual_category':
    case 'virtual_category': // eslint-disable-line no-fallthrough
    case 'category': {
      return (
        <StyledListItem
          name={item.name}
          link={link}
          image={
            styling !== 'simple' && item.logo ? (
              <img height="28" width="28" src={item.logo} alt="" />
            ) : null
          }
          callToActionText={callToActionText}
          showArrow
          onClick={onClickHandler}
        />
      );
    }
    default:
      return null;
  }
};

const { object, func, oneOf, string } = PropTypes;

SearchItem.propTypes = {
  item: object.isRequired,
  type: oneOf([
    'product',
    'raw',
    'raw_sorted',
    'category',
    'brand_category',
    'virtual_category',
    'brand_virtual_category',
    'store',
    'brand',
  ]).isRequired,
  styling: oneOf(['simple', 'rich']),
  mode: oneOf(['row', 'grid']),
  onRawClick: func, // Use for tracking
  onClick: func,
  onSelect: func,
  query: string.isRequired,
  callToActionText: string,
};

SearchItem.defaultProps = {
  mode: 'row',
  styling: 'rich',
  onRawClick: () => {},
  onClick: () => {},
  onSelect: undefined,
  callToActionText: undefined,
};

const Outlink = ({ store }) => {
  const { t } = useContext(AppContext);
  return (
    <span>
      {store}
      {' - '}
      <OutlinkCallToAction>
        {t('search', 'raw-search.go-to-shop')}
        <OutlinkIcon size={14} />
      </OutlinkCallToAction>
    </span>
  );
};

Outlink.contextType = AppContext;

Outlink.propTypes = {
  store: string.isRequired,
};

export default memo(SearchItem);
